// frontend/src/components/purchase-orders/usePurchaseOrderLifecycle.jsx
//
// Taking a lifecycle action on one purchase order — the one home for what
// Issue, Edit, Cancel, Close incomplete, Delete and Duplicate do, for the PO
// details page and for the PO list's row actions alike. Which actions a PO
// offers is ./poActions.js's call; this hook carries one out: the
// confirmation (with the delivery date Issue collects and the reason Cancel
// and Close collect), the request, and the announcement. Every write's
// mutation hook names the purchase orders as changed (hooks/usePOMutations),
// so the page and the list both read the new status back from the server.
//
// Receiving is the caller's, because it differs by screen: the details page
// fills the lines in place, the list opens the PO ready to receive. Delete
// tells the caller too, because the deleted PO's tab has to close.
//
// `run` keeps one identity while the callbacks do (TanStack's `mutateAsync`
// is stable), so the list's rows re-render only when `running` moves.
// `running` holds one entry per PO (hooks/useInFlight), so a write on one
// list row leaves every other row's buttons as they are.
//
//   const lifecycle = usePurchaseOrderLifecycle({
//     onReceive: (po) => …,      // PO_ACTION.RECEIVE
//     onDeleted: (po) => …,      // after a successful Delete
//   });
//   lifecycle.run(po, action)    // po: list or detail wire shape
//   lifecycle.run(po, PO_ACTION.ISSUE, { needByDate })
//                                // the day a draft's page picked; the issue
//                                // confirmation starts at it
//   lifecycle.running            // { [poId]: action } for each write in flight; {} when none
//   lifecycle.dialogs            // render once: the confirmation and the edit form

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { useInFlight } from '../../hooks/useInFlight';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  useIssuePO, useCancelPO, useClosePO, useDeletePO, useDuplicatePO,
} from '../../hooks/usePOMutations';
import { DATA, useDataChanged } from '../../queries/dataChanged';
import { apiJson, translateRequestError } from '../../queries/api';
import { useStoreToday } from '../../hooks/useStoreToday';
import { useOpenPurchaseOrder } from '../../hooks/useOpenPurchaseOrder';
import { showToast } from '../common/appToastBus';
import PurchaseOrderForm from './PurchaseOrderForm';
import { PO_ACTION } from './poActions';

export function usePurchaseOrderLifecycle({ onReceive, onDeleted }) {
  const { t } = useTranslation();
  const storeToday = useStoreToday();
  const { confirm, dialogProps } = useConfirmDialog();
  const openPurchaseOrder = useOpenPurchaseOrder();
  const changed = useDataChanged();
  const { mutateAsync: issuePO } = useIssuePO();
  const { mutateAsync: cancelPO } = useCancelPO();
  const { mutateAsync: closePO } = useClosePO();
  const { mutateAsync: deletePO } = useDeletePO();
  const { mutateAsync: duplicatePO } = useDuplicatePO();
  const { running, track } = useInFlight();
  // The draft whose edit form is open.
  const [editing, setEditing] = useState(null);

  // A PO's entry spans the request and the refresh the mutation awaits, so
  // its button spins until the new status is on screen.
  const write = useCallback(
    (po, action, request) => track(po.purchase_order_header_id, action, request),
    [track],
  );

  const runAction = useCallback(async (po, action, { mutate, vars, successKey, failureKey }) => {
    try {
      await write(po, action, () => mutate({ id: po.purchase_order_header_id, ...vars }));
      showToast(t(successKey, { poNumber: po.po_number }), 'success');
    } catch (error) {
      console.error(`${successKey}:`, error.message);
      showToast(translateRequestError(error, t, failureKey), 'error');
    }
  }, [write, t]);

  const run = useCallback(async (po, action, { needByDate = null } = {}) => {
    const confirmVars = { poNumber: po.po_number, supplier: po.supplier_name };
    switch (action) {
      case PO_ACTION.RECEIVE:
        onReceive(po);
        return;
      case PO_ACTION.EDIT:
        setEditing(po);
        return;
      case PO_ACTION.ISSUE: {
        // The confirmation's date field starts at the day the draft's page
        // picked, else at the delivery date issuing sets, read from the
        // server's own computation. The server's date is not sent back, so
        // each line keeps its own item's lead time; any other day lands on
        // every line.
        let proposed;
        try {
          ({ need_by_date: proposed } = await write(po, action, () => apiJson.get(
            `/api/po-calculations/purchase-orders/${po.purchase_order_header_id}/issue-date/`,
          )));
        } catch (error) {
          console.error('purchaseOrders.messages.issueDateFailed:', error.message);
          showToast(translateRequestError(error, t, 'purchaseOrders.messages.issueDateFailed'), 'error');
          return;
        }
        const ok = await confirm({
          title: t('purchaseOrders.messages.issueTitle'),
          message: t('purchaseOrders.messages.issueConfirm', confirmVars),
          confirmLabel: t('purchaseOrders.actions.issue'),
          variant: 'info',
          date: {
            label: t('purchaseOrderDetail.meta.expectedDeliveryDate'),
            initial: needByDate ?? proposed,
            min: storeToday,
          },
        });
        if (!ok) return;
        await runAction(po, action, {
          mutate: issuePO,
          vars: { needByDate: ok.value === proposed ? null : ok.value },
          successKey: 'purchaseOrders.messages.issueSuccess',
          failureKey: 'purchaseOrders.messages.issueFailed',
        });
        return;
      }
      case PO_ACTION.CANCEL: {
        // The reason is kept on the PO (`cancelled_reason`, shown in the meta
        // bar) — the gate collects it; the server bounds it (po_reason_too_long).
        const ok = await confirm({
          title: t('purchaseOrders.messages.cancelTitle'),
          message: t('purchaseOrders.messages.cancelConfirm', confirmVars),
          confirmLabel: t('purchaseOrders.messages.cancelPoLabel'),
          cancelLabel: t('purchaseOrders.messages.keepPoLabel'),
          variant: 'danger',
          input: { label: t('purchaseOrders.messages.reasonLabel') },
        });
        if (!ok) return;
        await runAction(po, action, {
          mutate: cancelPO,
          vars: { reason: ok.value },
          successKey: 'purchaseOrders.messages.cancelSuccess',
          failureKey: 'purchaseOrders.messages.cancelFailed',
        });
        return;
      }
      case PO_ACTION.CLOSE: {
        // An ERP-owned PO is closed short IN the ERP (lines reduced to what
        // was received) and reads as fully received on both sides; the copy
        // says so, because the outcome differs from a local close.
        const erp = !!po.is_erp_managed;
        const ok = await confirm({
          title: t('purchaseOrders.messages.closeTitle'),
          message: t(erp ? 'purchaseOrders.messages.closeConfirmErp' : 'purchaseOrders.messages.closeConfirm', confirmVars),
          confirmLabel: t('purchaseOrders.messages.closePoLabel'),
          cancelLabel: t('purchaseOrders.messages.keepPoLabel'),
          variant: 'danger',
          input: { label: t('purchaseOrders.messages.reasonLabel') },
        });
        if (!ok) return;
        await runAction(po, action, {
          mutate: closePO,
          vars: { reason: ok.value },
          successKey: erp ? 'purchaseOrders.messages.closeSuccessErp' : 'purchaseOrders.messages.closeSuccess',
          failureKey: 'purchaseOrders.messages.closeFailed',
        });
        return;
      }
      case PO_ACTION.DUPLICATE: {
        // The copy is a new draft; the merchant lands on it in its own tab.
        // A refusal (an item on the order can no longer be ordered from the
        // supplier) is named in the error.
        try {
          const copy = await write(po, action, () => duplicatePO({ id: po.purchase_order_header_id }));
          showToast(t('purchaseOrders.messages.duplicateSuccess', {
              poNumber: copy.po_number, sourcePoNumber: po.po_number,
            }), 'success');
          openPurchaseOrder(copy.purchase_order_header_id);
        } catch (error) {
          console.error('duplicatePO:', error.message);
          showToast(translateRequestError(error, t, 'purchaseOrders.messages.duplicateFailed'), 'error');
        }
        return;
      }
      case PO_ACTION.DELETE: {
        const ok = await confirm({
          title: t('common.deleteTitle'),
          message: t('purchaseOrders.messages.deleteConfirm', confirmVars),
          confirmLabel: t('common.delete'),
          variant: 'danger',
        });
        if (!ok) return;
        try {
          await write(po, action, () => deletePO(po.purchase_order_header_id));
          showToast(t('purchaseOrders.messages.deleteSuccess', { poNumber: po.po_number }), 'success');
          onDeleted(po);
        } catch (error) {
          console.error('deletePO:', error.message);
          showToast(translateRequestError(error, t, 'purchaseOrders.messages.deleteFailed'), 'error');
        }
        return;
      }
      default:
        throw new Error(`usePurchaseOrderLifecycle: unhandled action "${action}"`);
    }
  }, [confirm, t, storeToday, runAction, write, onReceive, onDeleted, issuePO, cancelPO, closePO, deletePO, duplicatePO, openPurchaseOrder]);

  const handleEditSaved = useCallback(({ status: savedStatus, messages }) => {
    const label = savedStatus === 'draft'
      ? t('purchaseOrders.create.draftUpdated')
      : t('purchaseOrders.create.createdAndIssued');
    showToast(`${label}: ${editing.po_number}`, 'success');
    setEditing(null);
    if (messages.length > 0) {
      setTimeout(() => showToast(messages.join(' | '), 'info'), 2000);
    }
    // The form saves outside TanStack (usePurchaseOrderDraft), so the
    // announcement every PO write ends in is wired here. A line's cost can
    // become the supplier's price and the item's cost of record.
    changed([DATA.purchaseOrders, DATA.items, DATA.supplierLinks]);
  }, [t, editing, changed]);

  // Mounted only while open, like every PurchaseOrderForm: the form carries
  // its own AG Grid, and the draft hook resets and loads on `open` anyway.
  const dialogs = (
    <>
      <ConfirmDialog {...dialogProps} />
      {editing && (
        <PurchaseOrderForm
          open
          onClose={() => setEditing(null)}
          onSaved={handleEditSaved}
          purchaseOrderId={editing.purchase_order_header_id}
        />
      )}
    </>
  );

  return { run, running, dialogs };
}
